import React, { useState, useEffect } from 'react';
import { historyApi } from '../../services/api';
import './History.css';

export default function History() {
    const [resumes, setResumes] = useState([]);
    const [selectedResume, setSelectedResume] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadHistory();
        loadStats();
    }, [currentPage]);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const offset = (currentPage - 1) * itemsPerPage;
            const response = await historyApi.getHistory(itemsPerPage, offset);
            setResumes(response.data.resumes || []);
            setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
            setError(null);
        } catch (err) {
            setError('فشل تحميل سجل السير الذاتية');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await historyApi.getStats();
            setStats(response.data);
        } catch (err) {
            console.error('فشل تحميل الإحصائيات:', err);
        }
    };

    const handleViewResume = async (resumeId) => {
        try {
            const response = await historyApi.getResume(resumeId);
            setSelectedResume(response.data);
        } catch (err) {
            setError('فشل تحميل تفاصيل السيرة الذاتية');
            console.error(err);
        }
    };

    const handleDeleteResume = async (resumeId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه السيرة الذاتية؟')) {
            return;
        }

        try {
            await historyApi.deleteResume(resumeId);
            setResumes(resumes.filter(r => r.id !== resumeId));
            if (selectedResume?.id === resumeId) {
                setSelectedResume(null);
            }
            await loadStats();
        } catch (err) {
            setError('فشل حذف السيرة الذاتية');
            console.error(err);
        }
    };

    const handleDownload = async (resumeId, jobTitle) => {
        try {
            const blob = await historyApi.downloadDocx(resumeId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `resume-${jobTitle.replace(/\s+/g, '-')}-${resumeId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError('فشل تحميل السيرة الذاتية');
            console.error(err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    if (loading && resumes.length === 0) {
        return (
            <div className="history-container">
                <div className="history-loading">جاري تحميل سجل السير الذاتية...</div>
            </div>
        );
    }

    return (
        <div className="history-container">
            <div className="history-header">
                <div>
                    <h2 className="history-title">سجل السير الذاتية</h2>
                    <p className="history-subtitle">عرض وإدارة السير الذاتية المُنشأة</p>
                </div>
                {stats && (
                    <div className="history-stats">
                        <div className="stat-item">
                            <span className="stat-label">إجمالي السير الذاتية</span>
                            <span className="stat-value">{stats.total_resumes}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">متوسط نسبة التطابق</span>
                            <span className="stat-value">{stats.avg_match_score?.toFixed(1)}%</span>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="history-error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                </div>
            )}

            <div className="history-content">
                <div className="history-list">
                    {resumes.length === 0 ? (
                        <div className="empty-state">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <h3 className="empty-state-title">لا توجد سير ذاتية بعد</h3>
                            <p className="empty-state-description">
                                أنشئ سيرتك الذاتية الأولى لتظهر هنا
                            </p>
                        </div>
                    ) : (
                        <>
                            {resumes.map((resume) => (
                                <div
                                    key={resume.id}
                                    className={`resume-card ${selectedResume?.id === resume.id ? 'active' : ''}`}
                                    onClick={() => handleViewResume(resume.id)}
                                >
                                    <div className="resume-card-header">
                                        <div className="resume-card-info">
                                            <h3 className="resume-card-title">
                                                {resume.job_title || 'سيرة ذاتية بدون عنوان'}
                                            </h3>
                                            <p className="resume-card-date">
                                                {formatDate(resume.created_at)}
                                            </p>
                                        </div>
                                        <div className="resume-card-actions" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                className="action-button download"
                                                onClick={() => handleDownload(resume.id, resume.job_title)}
                                                title="تحميل DOCX"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                            </button>
                                            <button
                                                className="action-button delete"
                                                onClick={() => handleDeleteResume(resume.id)}
                                                title="حذف"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="resume-card-meta">
                                        <div className="meta-item">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            <span>{resume.template || 'default'}</span>
                                        </div>
                                        {resume.match_score && (
                                            <div className="meta-item match-score">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                    <polyline points="22 4 12 14.01 9 11.01" />
                                                </svg>
                                                <span>{resume.match_score}% تطابق</span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="resume-card-description">
                                        {truncateText(resume.job_description)}
                                    </p>
                                </div>
                            ))}

                            {totalPages > 1 && (
                                <div className="pagination">
                                    <button
                                        className="pagination-button"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                        السابق
                                    </button>
                                    <span className="pagination-info">
                                        صفحة {currentPage} من {totalPages}
                                    </span>
                                    <button
                                        className="pagination-button"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        التالي
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {selectedResume && (
                    <div className="history-preview">
                        <div className="preview-header">
                            <h3 className="preview-title">معاينة السيرة الذاتية</h3>
                            <button
                                className="preview-close"
                                onClick={() => setSelectedResume(null)}
                                title="إغلاق المعاينة"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="preview-content">
                            <pre className="markdown-content">{selectedResume.content}</pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
